import { useState, useCallback, useMemo } from "react";
import styles from "./styles.module.scss";
import { AnalysisLstatLogsGraphInterface } from "../../../interfaces/fe.interfaces";
import AnalysisLstatConnectedScatterplot from "./AnalysisLstatConnectedScatterplot";
import AnalysisLstatHistogram from "./AnalysisLstatHistogram";
import { LSTAT_GRAPH_TYPES } from "../../../utils/constants";

export interface AnalysisLstatGraphProps {
  graph: AnalysisLstatLogsGraphInterface;
  width: number;
  height: number;
}

const createPlotDataPoint = (graph: AnalysisLstatLogsGraphInterface) => {
  return graph.y_coordinates_parsed[0].map((yCoordinate, i) => {
    if (graph.x_coordinates_parsed[0].length === graph.y_coordinates_parsed[0].length) {
      return ({
        x: graph.x_coordinates_parsed[0][i],
        y: yCoordinate
      });
    }

    return ({x: 0, y:0});
  });
};

function createSequence(start: number, end: number, step: number) {
  const sequence = [];
  for (let i = start; i <= end; i += step) {
    sequence.push(i);
  }
  return sequence;
}

const transformHistogramData = (graph: AnalysisLstatLogsGraphInterface) => {
  if (!!graph?.bins.length && !!graph?.blocks?.[0]) {
    return ({
      xDomain: graph.bins[0],
      data: graph.blocks[0]
    });
  } else if (graph.rwidth && graph.bins && graph.cat_data) {
    let max = Math.max(...graph.cat_data[0]);
    let min = Math.min(...graph.cat_data[0]);
    if (min === max) {
      min = min - 1;
      max = max + 1;
    }

    const binStep = (max - min) / graph.bins[0];
    const xDomain = createSequence(min, max, binStep);
    xDomain.push(xDomain[xDomain.length - 1] + binStep);

    const data = ({
      xDomain: xDomain,
      data: graph.cat_data[0]
    });

    return data;
  }

  return null;
};

const AnalysisLstatGraph = ({
  graph,
  width,
  height
}: AnalysisLstatGraphProps) => {
  const [graphType] = useState(graph.graph_type[0]);

  const histogramData = useMemo(() => {
    if (graphType === LSTAT_GRAPH_TYPES.Scatterplot) return null;
    const data = transformHistogramData(graph);
    return data;
  }, [graph]);

  const renderGraph = useCallback(() => {
    switch (graphType) {
      case LSTAT_GRAPH_TYPES.Histogram:
        return histogramData &&
          <AnalysisLstatHistogram
            width={width}
            height={height}
            data={histogramData.data}
            xDomain={histogramData.xDomain as number[]}
            graph={graph} />;
      case LSTAT_GRAPH_TYPES.Scatterplot:
        return <AnalysisLstatConnectedScatterplot
          width={width}
          height={height}
          data={createPlotDataPoint(graph)}
          graph={graph} />;
      default:
        return null;
    }
  }, [graphType, graph, width, height]);

  return <div className={styles.wisaAnalysisLstatGraphsGraph} >{renderGraph()}</div>;
};

export default AnalysisLstatGraph;